import { useEffect, useState } from "react";
import { getDataMngButtonTitle, globalAny, pushCreateAccountStateToBrowserHistory } from "../../utils/Utils";
import { AsyncStorageKeys, CreateAccountState, Pages, SubPages, Type } from "../../Types";
import { useNavigation } from "@react-navigation/native";
import { LoginScreenNavigationProp, routeCreateAccount, routeCreateAccountDetails, routeLogin, routePrivacyPolicy } from "../../Routes";
import { Legals } from "../../models/Legals";
import { AcceptOtherData } from "../../models/AcceptOtherData";
import CreateAccountForm from "../CreateAccountScreenComponents";
import LoadingIndicator from "../../components/LoadingIndicator";
import LegalTermsConditions from "../../components/LegalTermsConditions";
import OtherDataComponent from "../../components/OtherDataComponent";
import TopAppBar from "../../components/TopAppBar";
import backIcon from "../../assets/TopNavBarIcon/icon_nav_back.svg";
import closeIcon from "../../assets/TopNavBarIcon/icon_nav_close.svg";
import AcceptDataComponent from "../../components/AcceptDataComponent";
import PrivacyPolicy from "../../components/PrivacyPolicy";
import { AppLogger } from "../../utils/AppLogger";
import useToggleStore from "../../store/useToggle.store";
import { StyledOuterContainer } from "./styled";
import useGenericContentStore from "../../store/genericContent.store";
import LegalData from "../../components/LegalData";

const CreateAccountScreen = (props: any) => {
	const navigation = useNavigation<any>();
	const navigationLoginScreen = useNavigation<LoginScreenNavigationProp>();
	const { location, subLocation } = props.route.params;
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));
	const isToggleHideDataMgmt = useToggleStore((state: any) => state.isToggleHideDataMgmt);

	// UI flags
	const [showWelcomeSplash, setShowWelcomeSplash] = useState(false);
	const [showActivityIndicator, setShowActivityIndicator] = useState(true);

	// Data flags
	const [otherData, setOtherData] = useState(false);
	const [videoAnalyticsData, setVideoAnalyticsData] = useState(false);

	// Analytics & external data
	const [googleData, setGoogleData] = useState<any>({});
	const [googleAnalytics, setGoogleAnalytics] = useState("");
	const [videoAnalytics, setVideoAnalytics] = useState("");

	// Legal & accept data
	const [state, setState] = useState(globalAny.language.create_account);
	const [acceptOtherData, setAcceptOtherData] = useState<AcceptOtherData>();
	const [legalData, setLegalData] = useState<Legals>();
	const [legalText, setLegalText] = useState<Legals>();
	const [legalDescription, setLegalDescription] = useState("");

	// Sony & misc
	const [sonyPictures, setSonyPictures] = useState("");
	const [sonyCompany, setSonyCompany] = useState("");

	// Accept/other data copy
	const [acceptDataTitle, setAcceptDataTitle] = useState("");
	const [acceptDataDescription, setAcceptDataDescription] = useState("");
	const [otherDataTitle, setOtherDataTitle] = useState("");
	const [otherDataDescription, setOtherDataDescription] = useState("");

	// Flow state
	const [createAccountState, setCreateAccountState] = useState("");

	/**
	 * Handles the action when the "Next" button is pressed.
	 *
	 * Behavior:
	 * - If `isToggleHideDataMgmt` is enabled, navigates directly to the `createAccount` page.
	 * - Otherwise, navigates to the next page depending on the current `createAccountState`.
	 *
	 * Flow:
	 * - legalOne   → acceptData
	 * - legalTwo   → otherData
	 * - legalThree → legalTerms
	 * - legalFour  → createAccount
	 *
	 * @returns {void}
	 */
	const onNextButtonPress = () => {
		if (isToggleHideDataMgmt) {
			navigation.push(routeCreateAccount, { location: Pages.createAccount });
			return;
		}

		let location;
		switch (createAccountState) {
			case CreateAccountState.legalOne:
				location = Pages.acceptData;
				break;
			case CreateAccountState.legalTwo:
				location = Pages.otherData;
				break;
			case CreateAccountState.legalThree:
				location = Pages.legalTerms;
				break;
			case CreateAccountState.legalFour:
				location = Pages.createAccount;
				break;
			default:
				return;
		}

		navigation.push(routeCreateAccount, { location });
	};

	/**
	 * Handles navigation to the Privacy Policy screen.
	 *
	 * @returns {void}
	 */
	const onPrivacy = () => navigation.push(routePrivacyPolicy);

	/**
	 * Handles the action when the "Back" button is pressed.
	 *
	 * Behavior:
	 * - If the navigation stack allows going back, it navigates to the previous screen.
	 * - Otherwise, it navigates to the login screen (`routeLogin`).
	 *
	 * @returns {void}
	 */
	const onBackPress = () => {
		if (navigation.canGoBack()) {
			navigation.goBack();
			return;
		}

		navigation.navigate(routeLogin);
	};

	/**
	 * Loads and sets "Accept Data" content for a given company name and key.
	 *
	 * Behavior:
	 * - Attempts to retrieve `sectionWording` data from `acceptOtherData`.
	 * - If not available, falls back to cached data in `localStorage` (using `AsyncStorageKeys.cacheAcceptOtherDataCreate`).
	 * - Extracts the text value associated with the provided `key`.
	 * - Builds HTML markup for display if a matching text value is found.
	 * - Updates state based on the `name` parameter:
	 *   - If `name === "Sony Pictures"`, updates `setSonyPictures`.
	 *   - Otherwise, updates `setSonyCompany`.
	 * - Sets the current state using `setState(name)`.
	 * - Logs an error if data retrieval fails.
	 *
	 * @param {string} name - The company name to match (e.g., `"Sony Pictures"`).
	 * @param {string} key - The key used to find the corresponding text value in `sectionWording`.
	 * @returns {void}
	 */
	const loadAcceptData = (name: string, key: string) => {
		try {
			let currentAcceptData = acceptOtherData?.sections
				? acceptOtherData?.sections[0]?.sectionWording?.filter((val: any) => val?.key === key)
				: null;
			let sonyCompanyText = null;
			if (!currentAcceptData) {
				const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataCreate) ?? "{}");
				currentAcceptData = cacheAcceptOtherData?.sections
					? cacheAcceptOtherData?.sections[0]?.sectionWording?.filter((val: any) => val?.key === key)
					: [];
			}

			sonyCompanyText = currentAcceptData[0]?.textValue
				? `
			<span style="display: flex align-items: center">
				<label target="_blank" style="display: flex align-items: center margin-right: 500px margin-top:-100px">
				${currentAcceptData[0]?.textValue}
				</label>
			</span>
		`
				: "";

			name === "Sony Pictures" ? setSonyPictures(currentAcceptData[0]?.textValue) : setSonyCompany(sonyCompanyText);
			setState(name);
		} catch {
			AppLogger.log("Error Missing Legals");
		}
	};

	/**
	 * Handles the press action for "Sony Pictures".
	 *
	 * Behavior:
	 * - Calls {@link loadAcceptData} with:
	 *   - `name = "Sony Pictures"`
	 *   - `key = "section-read-more-1"`
	 * - Loads and updates the Accept Data state for Sony Pictures.
	 *
	 * @returns {void}
	 */
	const onPressSonyPictures = () => loadAcceptData("Sony Pictures", "section-read-more-1");

	/**
	 * Handles the press action for "Sony Group Companies".
	 *
	 * Behavior:
	 * - Calls {@link loadAcceptData} with:
	 *   - `name = "Sony Group Companies"`
	 *   - `key = "section-read-more-2"`
	 * - Loads and updates the Accept Data state for Sony Group Companies.
	 *
	 * @returns {void}
	 */
	const onPressSonyCompany = () => loadAcceptData("Sony Group Companies", "section-read-more-2");

	/**
	 * Loads and sets "Other Data" content for a given option name and key.
	 *
	 * Behavior:
	 * - Tries to fetch option data from `acceptOtherData.sections[0].childSections[0].options`.
	 * - If unavailable, falls back to cached data from `localStorage`
	 *   (`AsyncStorageKeys.cacheAcceptOtherDataSettings`).
	 * - Extracts the matching text value from `optionsWordings` using the provided `key`.
	 * - Also retrieves the `option-title` wording to update state.
	 * - Updates state depending on the `name` parameter:
	 *   - If `name === "Google Analytics"`, updates `setGoogleAnalytics`.
	 *   - Otherwise, updates `setVideoAnalytics`.
	 * - Calls `setState` with the option title.
	 * - Returns the matched text value, if found.
	 * - Logs an error if data retrieval fails.
	 *
	 * @param {string} name - The option name to match (e.g., `"Google Analytics"`, `"Video Analytics"`).
	 * @param {string} key - The key used to locate the corresponding text value in `optionsWordings`.
	 * @returns {string | undefined} The text value of the matched option, or `undefined` if not found.
	 */
	const loadOtherData = (name: string, key: string) => {
		try {
			let initialData = acceptOtherData?.sections[0]?.childSections[0].options?.filter((val: any) => val?.name === name);
			if (!initialData) {
				const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataSettings) ?? "{}");
				initialData = cacheAcceptOtherData?.sections[0]?.childSections[0].options?.filter((val: any) => val?.name === name);
			}
			const data = initialData[0]?.optionsWordings?.filter((val: any) => val?.key === key);
			const title = initialData[0]?.optionsWordings?.filter((val: any) => val?.key === "option-title");
			name === "Google Analytics" ? setGoogleAnalytics(data[0]?.textValue) : setVideoAnalytics(data[0]?.textValue);
			setState(title[0]?.textValue);
			return data[0]?.textValue;
		} catch {
			AppLogger.error("Error Missing Legals");
		}
	};

	/**
	 * Handles the press action for "Google Analytics".
	 *
	 * Behavior:
	 * - Calls {@link loadOtherData} with:
	 *   - `name = "Google Analytics"`
	 *   - `key = "option-read-more-1"`
	 * - If no data is returned:
	 *   - Updates state with a fallback title from {@link getDataMngButtonTitle}.
	 * - Updates `setGoogleAnalytics` with the retrieved or fallback data.
	 *
	 * @returns {void}
	 */
	const onPressGoogle = () => {
		const isGoogleAnalytics = loadOtherData("Google Analytics", "option-read-more-1");
		!isGoogleAnalytics && setState(getDataMngButtonTitle("Google Analytics", globalAny.language.google_analytics));
		setGoogleAnalytics(isGoogleAnalytics);
	};

	/**
	 * Handles the press action for "Video Analytics".
	 *
	 * Behavior:
	 * - Calls {@link loadOtherData} with:
	 *   - `name = "Video Analytics"`
	 *   - `key = "option-read-more-1"`
	 * - If no data is returned:
	 *   - Updates state with a fallback title from {@link getDataMngButtonTitle}.
	 * - Updates `setVideoAnalytics` with the retrieved or fallback data.
	 *
	 * @returns {void}
	 */
	const onPressVideoAnalytics = () => {
		const isVideoAnalytics = loadOtherData("Video Analytics", "option-read-more-1");
		!isVideoAnalytics && setState(getDataMngButtonTitle("Video Analytics", globalAny.language.video_analytics));
		setVideoAnalytics(isVideoAnalytics);
	};

	/**
	 * Loads and displays legal content based on a given key and type.
	 *
	 * Behavior:
	 * - Attempts to fetch `legalContents` from `legalData`.
	 * - If unavailable, falls back to cached data in `localStorage`
	 *   (`AsyncStorageKeys.cacheLegalDataCreate`).
	 * - Finds the matching legal content by `type`.
	 * - Depending on the `keyValue`:
	 *   - Updates state with the selected legal title (e.g., "Promotional Terms", "Terms of Use", "Terms of Service").
	 *   - Sets the corresponding HTML content using `setLegalText`.
	 *
	 * @param {string} keyValue - The language key for the legal content (e.g., `globalAny.language.promotional_terms`).
	 * @param {number} type - The numeric type identifier for the legal content.
	 * @returns {Promise<void>} Resolves when the state and content have been updated.
	 */
	const onLegal = async (keyValue: string, type: number) => {
		let data = legalData?.legalContents?.filter((val: any) => val?.type === type);
		if (!data) {
			const cacheLegalData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheLegalDataCreate) ?? "{}");
			data = cacheLegalData?.legalContents?.filter((val: any) => val?.type === type);
		}
		if (data) {
			switch (keyValue) {
				case globalAny.language.promotional_terms:
					setState(globalAny.language.promotional_terms);
					setLegalText(data[0]?.htmlContent);
					break;
				case globalAny.language.terms_of_use:
					setState(globalAny.language.terms_of_use);
					setLegalText(data[0]?.htmlContent);
					break;

				case globalAny.language.terms_of_service:
					setState(globalAny.language.terms_of_service);
					setLegalText(data[0]?.htmlContent);
					break;
			}
		}
	};

	/**
	 * Handles closing the create account flow.
	 *
	 * Behavior:
	 * - Resets the `createAccountState` to `legalOne`.
	 * - Pushes the reset state to browser history.
	 * - Navigates to the login screen (`routeLogin`).
	 *
	 * @returns {void}
	 */
	const onClose = () => {
		setCreateAccountState(CreateAccountState.legalOne);
		pushCreateAccountStateToBrowserHistory(CreateAccountState.legalOne);
		navigationLoginScreen.navigate(routeLogin, {});
	};

	/**
	 * Fetches the legal description text for display.
	 *
	 * Behavior:
	 * - Attempts to retrieve `sectionWording` from `acceptOtherData`.
	 * - If `acceptOtherData` is unavailable, falls back to cached data in `localStorage`
	 *   (`AsyncStorageKeys.cacheAcceptOtherDataCreate`).
	 * - Searches for the wording with key `"section-description"`.
	 * - Updates the legal description state using `setLegalDescription`.
	 * - If no description is found, defaults to `globalAny.language.legal_one_body`.
	 *
	 * @returns {void}
	 */
	const fetchLegalData = () => {
		let titleDescription;
		if (!acceptOtherData) {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataCreate) ?? "{}");

			if (!cacheAcceptOtherData) return;

			titleDescription = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.sectionWording?.find((val: any) => val?.key === "section-description")
				: null;
			setLegalDescription(titleDescription?.textValue ?? globalAny.language.legal_one_body);
			return;
		}

		titleDescription = acceptOtherData?.sections
			? acceptOtherData?.sections[0]?.sectionWording?.find((val: any) => val?.key === "section-description")
			: null;
		setLegalDescription(titleDescription?.textValue ?? globalAny.language.legal_one_body);
	};

	/**
	 * Fetches the Accept Data title and description for display.
	 *
	 * Behavior:
	 * - Attempts to retrieve `optionsWordings` from `acceptOtherData`.
	 * - If `acceptOtherData` is unavailable, falls back to cached data in `localStorage`
	 *   (`AsyncStorageKeys.cacheAcceptOtherDataCreate`).
	 * - Searches for the wording with keys `"option-title"` and `"option-description"`.
	 * - Updates the state using:
	 *   - `setAcceptDataTitle` for the title.
	 *   - `setAcceptDataDescription` for the description.
	 *
	 * @returns {void}
	 */
	const fetchAcceptData = () => {
		let titleData;
		let titleDescription;
		if (!acceptOtherData) {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataCreate) ?? "{}");

			if (!cacheAcceptOtherData) return;

			titleData = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-title")
				: null;
			titleDescription = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-description")
				: null;
			setAcceptDataTitle(titleData?.textValue);
			setAcceptDataDescription(titleDescription?.textValue);
			return;
		}

		titleData = acceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-title");
		titleDescription = acceptOtherData?.sections[0]?.options[0]?.optionsWordings?.find((val: any) => val?.key === "option-description");
		setAcceptDataTitle(titleData?.textValue);
		setAcceptDataDescription(titleDescription?.textValue);
	};

	/**
	 * Fetches the "Other Data" title and description for display.
	 *
	 * Behavior:
	 * - Attempts to retrieve `sectionWording` from `acceptOtherData.sections[0].childSections[0]`.
	 * - If `acceptOtherData` is unavailable, falls back to cached data in `localStorage`
	 *   (`AsyncStorageKeys.cacheAcceptOtherDataCreate`).
	 * - Searches for wording with keys `"section-title"` and `"section-description"`.
	 * - Updates the state using:
	 *   - `setOtherDataTitle` for the title.
	 *   - `setOtherDataDescription` for the description.
	 *
	 * @returns {void}
	 */
	const fetchOtherData = () => {
		let titleData;
		let titleDescription;
		if (!acceptOtherData) {
			const cacheAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataCreate) ?? "{}");

			if (!cacheAcceptOtherData) return;

			titleData = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-title")
				: null;
			titleDescription = cacheAcceptOtherData?.sections
				? cacheAcceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-description")
				: null;
			setOtherDataTitle(titleData?.textValue);
			setOtherDataDescription(titleDescription?.textValue);
			return;
		}

		titleData = acceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-title");
		titleDescription = acceptOtherData?.sections[0]?.childSections[0]?.sectionWording?.find((val: any) => val?.key === "section-description");
		setOtherDataTitle(titleData?.textValue);
		setOtherDataDescription(titleDescription?.textValue);
	};

	/**
	 * Initializes legal and other data when the component mounts.
	 *
	 * Behavior (executed once on mount):
	 * - Retrieves cached legal data and accept-other-data from `localStorage`.
	 * - Sets the retrieved data into state (`setLegalData`, `setAcceptOtherData`).
	 * - Calls helper functions to fetch and load initial content:
	 *   - `fetchLegalData()` → sets the main legal description.
	 *   - `loadAcceptData()` → loads Sony Pictures and Sony Group Companies data.
	 *   - `loadOtherData()` → loads Google Analytics and Video Analytics data.
	 * - Sets the initial UI state with `setState`.
	 * - Hides the activity indicator (`setShowActivityIndicator(false)`).
	 *
	 * @returns {void}
	 */
	useEffect(() => {
		(async () => {
			const resultLegal = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheLegalDataCreate) ?? "{}");
			const resultAcceptOtherData = JSON.parse(localStorage.getItem(AsyncStorageKeys.cacheAcceptOtherDataCreate) ?? "{}");
			setLegalData(resultLegal);
			setAcceptOtherData(resultAcceptOtherData);
			fetchLegalData();
			loadAcceptData("Sony Pictures", "section-read-more-1");
			loadAcceptData("Sony Group Companies", "section-read-more-2");
			loadOtherData("Google Analytics", "option-read-more-1");
			loadOtherData("Video Analytics", "option-read-more-1");
			setState(globalAny.language.create_account);
			setShowActivityIndicator(false);
		})();
	}, []);

	/**
	 * Updates the visibility state of "Other Data" based on analytics data.
	 *
	 * Behavior:
	 * - If neither `googleData` nor `videoAnalyticsData` exist, hides the other data section (`setOtherData(false)`).
	 * - If both `googleData` and `videoAnalyticsData` exist, shows the other data section (`setOtherData(true)`).
	 *
	 * This effect runs whenever `googleData` or `videoAnalyticsData` changes.
	 *
	 * @returns {void}
	 */
	useEffect(() => {
		if (!googleData && !videoAnalyticsData) {
			setOtherData(false);
		}

		if (googleData && videoAnalyticsData) {
			setOtherData(true);
		}
	}, [googleData, videoAnalyticsData]);

	/**
	 * Synchronizes the analytics data states with the `otherData` flag.
	 *
	 * Behavior:
	 * - Updates both `googleData` and `videoAnalyticsData` to match the value of `otherData`.
	 *
	 * This effect runs whenever `otherData` changes.
	 *
	 * @returns {void}
	 */
	useEffect(() => {
		setGoogleData(otherData);
		setVideoAnalyticsData(otherData);
	}, [otherData]);

	/**
	 * Updates the component state and loads data based on the current location or sub-location.
	 *
	 * Behavior:
	 * - Checks `subLocation` first and executes the corresponding action:
	 *   - `SubPages.termsOfUse` → calls `onLegal` with terms of use data.
	 *   - `SubPages.termsOfService` → calls `onLegal` with terms of service data.
	 *   - `SubPages.sonyPictures` → calls `onPressSonyPictures`.
	 *   - `SubPages.sonyGroup` → calls `onPressSonyCompany`.
	 *   - `SubPages.googleAnalytics` → calls `onPressGoogle`.
	 *   - `SubPages.videoAnalytics` → calls `onPressVideoAnalytics`.
	 * - If no matching `subLocation`, checks `location` and updates state or fetches data:
	 *   - `Pages.legal` → sets state to `legalOne`.
	 *   - `Pages.acceptData` → fetches accept data and sets state to `legalTwo`.
	 *   - `Pages.otherData` → fetches other data and sets state to `legalThree`.
	 *   - `Pages.legalTerms` → sets state to `legalFour`.
	 *   - `Pages.createAccount` → sets state to `newCreateAccountForm`.
	 *   - `Pages.privacy` → sets state to display the privacy policy.
	 *
	 * This effect runs whenever `location` or `subLocation` changes.
	 *
	 * @returns {void}
	 */
	useEffect(() => {
		switch (subLocation) {
			case SubPages.termsOfUse:
				onLegal(globalAny.language.terms_of_use, 6);
				return;
			case SubPages.termsOfService:
				onLegal(globalAny.language.terms_of_service, 3);
				return;
			case SubPages.sonyPictures:
				onPressSonyPictures();
				return;
			case SubPages.sonyGroup:
				onPressSonyCompany();
				return;
			case SubPages.googleAnalytics:
				onPressGoogle();
				return;
			case SubPages.videoAnalytics:
				onPressVideoAnalytics();
				return;
		}

		switch (location) {
			case Pages.legal:
				setCreateAccountState(CreateAccountState.legalOne);
				break;
			case Pages.acceptData:
				fetchAcceptData();
				setCreateAccountState(CreateAccountState.legalTwo);
				break;
			case Pages.otherData:
				fetchOtherData();
				setCreateAccountState(CreateAccountState.legalThree);
				break;
			case Pages.legalTerms:
				setCreateAccountState(CreateAccountState.legalFour);
				break;
			case Pages.createAccount:
				setCreateAccountState(CreateAccountState.newCreateAccountForm);
				break;
			case Pages.privacy:
				setState(globalAny.language.privacy_policy);
				break;
		}
	}, [location, subLocation]);

	if (showWelcomeSplash) {
		return <LoadingIndicator show={true} text={globalAny.language.account_created} showSpinner={true} />;
	}

	if (showActivityIndicator) {
		return <LoadingIndicator show={showActivityIndicator}></LoadingIndicator>;
	}

	return (
		<StyledOuterContainer>
			<TopAppBar
				leftIcon={closeIcon}
				leftIconWidth={92}
				leftTitleIcon={backIcon}
				screenTitle={state}
				onPressLeftIcon={onClose}
				onPressLeftTitleIcon={onBackPress}
				isSingleLogo={true}
				isNarrow={isNarrow}
			/>

			{createAccountState === CreateAccountState.newCreateAccountForm && !subLocation && (
				<CreateAccountForm
					accountInfo={{
						ageChecked: false,
						acceptTC: true,
						acceptPrivacy: true,
						email: "",
						password: "",
						confirmPassword: "",
						month: 0,
						year: 0,
						deviceIdentifier: `WEB-MERCEDES`,
						deviceModel: "WEB",
						softwareVersion: "6",
					}}
					onPressBack={() => onBackPress()}
					setShowWelcomeSplash={setShowWelcomeSplash}
					onPressPrivacy={onPrivacy}
				/>
			)}
			{createAccountState == CreateAccountState.legalOne &&
				!subLocation &&
				(isToggleHideDataMgmt ? (
					<LegalTermsConditions
						bodyText={globalAny.language.i_read_agree}
						showButton={true}
						onPressNext={() => onNextButtonPress()}
						onPressTermsOfUse={() =>
							navigation.push(routeCreateAccountDetails, { location: Pages.legalTerms, subLocation: SubPages.termsOfUse })
						}
						onPressTermsOfService={() =>
							navigation.push(routeCreateAccountDetails, { location: Pages.legalTerms, subLocation: SubPages.termsOfService })
						}
						onPressPrivacy={onPrivacy}
					/>
				) : (
					<LegalData
						bodyText={legalDescription ?? globalAny.language.legal_one_body}
						showButton={true}
						onPressNext={() => onNextButtonPress()}
						onPressPrivacy={onPrivacy}
					/>
				))}
			{createAccountState == CreateAccountState.legalTwo && !subLocation && (
				<AcceptDataComponent
					onPressSecondaryButton={() => onBackPress()}
					onPressPrimaryButton={() => onNextButtonPress()}
					onPressSonyCompany={() =>
						navigation.push(routeCreateAccountDetails, { location: Pages.acceptData, subLocation: SubPages.sonyGroup })
					}
					onPressSonyPictures={() =>
						navigation.push(routeCreateAccountDetails, { location: Pages.acceptData, subLocation: SubPages.sonyPictures })
					}
					onPressPrivacy={() => onPrivacy()}
					showPrivacyLink={true}
					type={Type.CreateAccount}
					title={acceptDataTitle}
					description={acceptDataDescription}
					sonyPictures={sonyPictures}
					sonyCompany={sonyCompany}
				/>
			)}

			{createAccountState == CreateAccountState.legalThree && !subLocation && (
				<OtherDataComponent
					onSubmit={(val) => setOtherData(val)}
					onChangeGoogleData={(val) => setGoogleData(val)}
					onChangeVideoAnalyticsData={(val) => setVideoAnalyticsData(val)}
					onPressSecondaryButton={onBackPress}
					onPressPrimaryButton={onNextButtonPress}
					onPressGoogle={() =>
						navigation.push(routeCreateAccountDetails, { location: Pages.otherData, subLocation: SubPages.googleAnalytics })
					}
					onPressVideoAnalytics={() =>
						navigation.push(routeCreateAccountDetails, { location: Pages.otherData, subLocation: SubPages.videoAnalytics })
					}
					onPressPrivacy={onPrivacy}
					showPrivacyLink={true}
					title={otherDataTitle}
					description={otherDataDescription}
					googleAnalytics={googleAnalytics}
					videoAnalytics={videoAnalytics}
				/>
			)}

			{createAccountState == CreateAccountState.legalFour && !subLocation && (
				<LegalTermsConditions
					bodyText={globalAny.language.i_read_agree}
					showButton={true}
					onPressBack={() => onBackPress()}
					onPressNext={() => onNextButtonPress()}
					onPressTermsOfUse={() =>
						navigation.push(routeCreateAccountDetails, { location: Pages.legalTerms, subLocation: SubPages.termsOfUse })
					}
					onPressTermsOfService={() =>
						navigation.push(routeCreateAccountDetails, { location: Pages.legalTerms, subLocation: SubPages.termsOfService })
					}
					onPressPrivacy={onPrivacy}
				/>
			)}

			{subLocation === SubPages.sonyPictures && <PrivacyPolicy text={sonyPictures} />}

			{subLocation === SubPages.sonyGroup && <PrivacyPolicy text={sonyCompany} />}

			{subLocation === SubPages.googleAnalytics && <PrivacyPolicy text={googleAnalytics} />}

			{subLocation === SubPages.videoAnalytics && <PrivacyPolicy text={videoAnalytics} />}

			{(subLocation === SubPages.termsOfUse || subLocation === SubPages.termsOfService) && <PrivacyPolicy text={legalText} />}
		</StyledOuterContainer>
	);
};

export default CreateAccountScreen;
